/*
 * Copyright © 2023. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromIban,
  fromInsurer,
  fromNumber,
  handleSimpleArray,
  pickAndConvert,
  reduceSimpleArray,
  sanitizeAddressData,
  sanitizeOrganizationEmailData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toIban,
  toInsurer,
  toNumber,
  trimValueIfSet
} from "@/src/services/formServiceFunctions";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import _forEach from "lodash/forEach";
import _set from "lodash/set";
import _get from "lodash/get";
import _cloneDeep from "lodash/cloneDeep";
import ApiClient from "@/src/services/apiClient";
import { getProductData } from "@/src/services/productService";
import _isEmpty from "lodash/isEmpty";
import { ConversionField, FormServiceBase, FormServiceSaveProps, FormStep } from "@/src/types/FormServiceBase";
import { Organization } from "@/src/types/Organization";
import { Customer } from "@/src/types/Entities/Customer";
import { Settlement } from "@/src/types/Entities/Settlement";
import { gaEvent } from "@/src/services/googleAnalyticsService";
import dayjs from "dayjs";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_TERMINATION]: {
      settlement: ["contractTerminated", "terminationObstacles", "contractTerminationReceivedDate", "terminationNoticeType", "preferredOutcome"],
      "client.bankData": ["iban"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT]: {
      settlement: ["employedSince", "employedSinceYear", "employedSinceMonth", "grossSalary", "jobDescription", "numberOfEmployees", "worksCouncil"],
      employerOrganization: ["name", "telephone"],
      "employerOrganization.mainEmail": ["email"],
      "employerOrganization.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_LEGAL_INSURANCE]: {
      settlement: ["legalExpenseInsurance", "noWinNoFee", "paymentType"],
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_PERSONAL]: {
      settlement: ["maritalStatus", "partnerIsWorking", "numberChildren"],
      client: ["gender", "givenName", "familyName", "birthDate"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone", "contactTime"],
    },
    [applicationStatuses.APPLICATION_STATUS_SUMMARY]: {
      settlement: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      settlement: ["powerOfAttorney"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {
      settlement: [],
    },
  });
  conversionFields = [
    {
      path: "client.bankData.iban",
      convertFrom: fromIban,
      convertTo: toIban,
    },
    {
      path: "contractTerminated",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "worksCouncil",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "partnerIsWorking",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "grossSalary",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "numberChildren",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "caseInsurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "numberOfEmployees",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "terminationObstacles",
      convertFrom: reduceSimpleArray,
      convertTo: handleSimpleArray,
    },
    {
      path: "contractTerminationReceivedDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "client.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "powerOfAttorney",
      convertFrom: (value: string | null) => !!value,
      convertTo: () => new Date(),
    },
    {
      path: "caseInsurance.insurer",
      convertFrom: fromInsurer,
      convertTo: toInsurer,
    },
  ];

  save = async ({ formData, applicationStatus, nextApplicationStatus, product, customer, updateMutation }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};
    const resultData = this.handleSettlementFields(formData);

    if (_has(fields, "customer")) {
      const customerData = _pick(resultData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client")) {
      let clientData = _pick(resultData.client, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      let addressData = _pick(resultData.client.residenceAddress, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "employerOrganization") && product?.employerOrganization) {
      let employerData = _pick(resultData.employerOrganization, fields.employerOrganization);
      employerData = this.sanitizeEmployerData(employerData);
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: product.employerOrganization.id,
        data: employerData,
      });
    }

    if (_has(fields, "employerOrganization.mainEmail")) {
      let emailData = pickAndConvert(resultData.employerOrganization.mainEmail, fields["employerOrganization.mainEmail"]);
      emailData = sanitizeOrganizationEmailData(emailData);
      await updateMutation.mutateAsync({
        uri: "/organization_emails",
        id: product.employerOrganization.mainEmail.id,
        data: emailData,
      });
    }

    if (_has(fields, "employerOrganization.address") && !!product.employerOrganization?.address?.id) {
      let addressData = _pick(resultData.employerOrganization.address, fields["employerOrganization.address"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.employerOrganization.address.id,
        data: addressData,
      });
    }

    if (_has(fields, "caseInsurance")) {
      const caseInsuranceData = pickAndConvert(resultData.caseInsurance, fields["caseInsurance"]);
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "client.bankData")) {
      const bankData = pickAndConvert(resultData.client.bankData, fields["client.bankData"]);

      if (bankData.iban === "DE") {
        delete bankData.iban;
      }

      if (!_isEmpty(bankData)) {
        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: formData.client.bankData.id,
          data: bankData,
        });
      }
    }

    if (_has(fields, "caseInsurance.policyHolder")) {
      const basePolicyHolderData = formData.caseInsurance.policyHolderType === "me" ? resultData.customer.person : resultData.caseInsurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["caseInsurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.caseInsurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    let settlementData: Partial<Settlement> = {};
    if (nextApplicationStatus) {
      settlementData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }
    if (_has(fields, "settlement")) {
      settlementData = _merge(settlementData, _pick(resultData, fields.settlement));
    }

    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: settlementData,
    });
  };

  sanitizeEmployerData = (employerData: Partial<Organization>) => {
    let sanitizedEmployerData = _cloneDeep(employerData);
    sanitizedEmployerData = trimValueIfSet("name", sanitizedEmployerData);
    sanitizedEmployerData = trimValueIfSet("telephone", sanitizedEmployerData);
    sanitizedEmployerData = trimValueIfSet("email", sanitizedEmployerData);
    return sanitizedEmployerData;
  };

  handleSettlementFields = (data: FormServiceSaveProps<ApplicationStatus>["formData"]) => {
    let resultData = _cloneDeep(data);
    _forEach(this.conversionFields, (conversionField) => {
      const { path, convertTo } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertTo(_get(resultData, path)));
      }
    });
    if (_has(resultData, "employedSinceYear") && _has(resultData, "employedSinceMonth")) {
      resultData.employedSince = `${resultData.employedSinceYear}-${resultData.employedSinceMonth}-01`;
      resultData.employedSinceYear = resultData.employedSinceMonth = undefined;
    }
    resultData.noWinNoFee = !resultData.legalExpenseInsurance;
    resultData.paymentType = resultData.legalExpenseInsurance ? "insurance" : "nwnf";

    return resultData;
  };

  prepareFormData = (product: Settlement, customer: Customer) => {
    let resultData = _cloneDeep(product);
    resultData.customer = _cloneDeep(customer);
    _forEach(this.conversionFields, (conversionField: ConversionField) => {
      const { path, convertFrom } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertFrom(_get(resultData, path)));
      }
    });
    if (resultData.employedSince) {
      const employedSince = dayjs(resultData.employedSince);
      resultData.employedSinceMonth = employedSince.format("MM");
      resultData.employedSinceYear = employedSince.format("YYYY");
    } else {
      resultData.employedSinceMonth = null;
      resultData.employedSinceYear = null;
    }
    return resultData;
  };

  statusFunctions: { [k in ApplicationStatus]?: () => void } = {
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: () => {
      gaEvent({
        category: "Document Upload",
        action: "Settlement Documents uploaded",
      });
    },
  };

  getAvailableSteps = () => {
    let availableSteps: FormStep[] = [];
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_TERMINATION]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_LEGAL_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PERSONAL]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    return availableSteps;
  };
}

export default new FormService();
